import { useQuery } from "@tanstack/react-query";
import { GitCompare, AlertTriangle, Check, Minus } from "lucide-react";
import { fetchSnapshot, fetchBias } from "@/lib/api";
import { goldUsdCrossRead } from "@/lib/crossReads";

const posCls = (d) => (d === "up" ? "text-pos" : d === "down" ? "text-neg" : "text-zinc-400");
const arrow = (d) => (d === "up" ? "▲" : d === "down" ? "▼" : "•");

const STATUS = {
  conflict: { label: "Conflict", cls: "text-neg border-red-500/50", Icon: AlertTriangle },
  aligned: { label: "Aligned", cls: "text-pos border-emerald-500/50", Icon: Check },
  neutral: { label: "Neutral", cls: "text-zinc-400 border-zinc-700", Icon: Minus },
};

export default function CommoditiesCrossRead() {
  const snapQ = useQuery({ queryKey: ["snapshot"], queryFn: fetchSnapshot, staleTime: 1000 * 60 * 10 });
  const usdQ = useQuery({ queryKey: ["bias", "USD"], queryFn: () => fetchBias("USD"), staleTime: 1000 * 60 * 15 });

  const gold = (snapQ.data?.markets || []).find((m) => m.symbol === "GOLD");
  const { status, gold: g, usd: u, note } = goldUsdCrossRead(gold, usdQ.data);
  const conf = usdQ.data?.confidence ?? 0;
  const st = STATUS[status];
  const loading = snapQ.isLoading || usdQ.isLoading;

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="commodities-cross-read">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
          <GitCompare size={11} strokeWidth={1.5} className="text-amber-term" /> Gold ⇄ USD Cross-Read
        </span>
        {!loading && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest border ${st.cls} ${status === "conflict" ? "animate-pulse" : ""}`}
            data-testid="cross-read-status"
          >
            <st.Icon size={10} strokeWidth={2} /> {st.label}
          </span>
        )}
      </div>

      {loading ? (
        <div className="h-[120px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">
          Reading gold vs dollar…
        </div>
      ) : (
        <div className="px-3 py-2.5 space-y-2">
          <div className="flex items-center justify-between" data-testid="cross-read-gold">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">Gold positioning</span>
            <span className={`text-[11px] uppercase tracking-widest tabular-nums font-semibold ${posCls(g.dir)}`}>
              {arrow(g.dir)} {g.label}
              {g.idx != null && <span className="text-zinc-600 font-normal"> · COT idx {g.idx}</span>}
            </span>
          </div>
          <div className="flex items-center justify-between" data-testid="cross-read-usd">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">USD news bias</span>
            <span className={`text-[11px] uppercase tracking-widest tabular-nums font-semibold ${posCls(u.dir)}`}>
              {arrow(u.dir)} {u.label}
              {conf > 0 && <span className="text-zinc-600 font-normal"> · {conf}% conf</span>}
            </span>
          </div>
          <p className="text-[11px] text-zinc-300 leading-relaxed pt-1 border-t border-zinc-900">{note}</p>
        </div>
      )}
    </div>
  );
}
